import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LOGO } from "@/lib/constants";
import { generalWaLink } from "@/lib/whatsapp";

const SECTIONS = ["home", "about", "services", "prices", "institute", "contact"];
const SECTION_IDS = { home: "hero", about: "about", services: "services", prices: "services", institute: "institute", contact: "contact" };

export default function Header({ contact }) {
  const { t, lang, setLang, langs, isRTL } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (key) => {
    setMenuOpen(false);
    const el = document.getElementById(SECTION_IDS[key]);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const phone = contact?.phone_intl || "972555004933";
  const bookLink = generalWaLink(phone, lang);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-ivory/85 backdrop-blur-md border-b border-sand/60" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
        <button onClick={() => goto("home")} className="flex items-center gap-3" data-testid="logo-button" aria-label="L'Éclat du Regard">
          <img src={LOGO} alt="L'Éclat du Regard" className="h-11 w-11 md:h-14 md:w-14 rounded-full object-cover ring-1 ring-gold/40" />
          <span className="hidden sm:block font-serif text-lg md:text-xl text-ink leading-none">
            {t.hero.brand}
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {SECTIONS.map((s) => (
            <button
              key={s}
              onClick={() => goto(s)}
              className="text-sm tracking-wide text-taupe hover:text-gold transition-colors"
              data-testid={`nav-${s}`}
            >
              {t.nav[s]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="flex items-center gap-1 px-3 py-2 text-sm text-ink hover:text-gold transition-colors rounded-full"
              data-testid="language-switcher"
              aria-label="Language"
            >
              {langs.find((l) => l.code === lang)?.label}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute end-0 mt-1 w-32 bg-white rounded-md border border-sand shadow-sm overflow-hidden"
                  data-testid="language-menu"
                >
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onMouseDown={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-start px-4 py-2.5 text-sm hover:bg-cream transition-colors ${lang === l.code ? "text-gold" : "text-ink"}`}
                      data-testid={`lang-option-${l.code}`}
                    >
                      {l.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            href={bookLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-white hover:brightness-105 transition-[filter,transform] hover:scale-[1.03]"
            data-testid="header-book-button"
          >
            {t.nav.book}
          </a>

          <button
            onClick={() => setMenuOpen(true)}
            className="lg:hidden p-2 text-ink"
            data-testid="mobile-menu-open"
            aria-label="Menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/30 lg:hidden"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-menu-overlay"
          >
            <motion.div
              initial={{ x: isRTL ? "-100%" : "100%" }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? "-100%" : "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-y-0 end-0 w-[82%] max-w-sm bg-ivory p-7 flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-menu-panel"
            >
              <div className="flex items-center justify-between mb-10">
                <img src={LOGO} alt="" className="h-12 w-12 rounded-full object-cover" />
                <button onClick={() => setMenuOpen(false)} className="p-2 text-ink" data-testid="mobile-menu-close" aria-label="Close">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {SECTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => goto(s)}
                    className="text-start text-2xl font-serif text-ink py-3 border-b border-sand/50 hover:text-gold transition-colors"
                    data-testid={`mobile-nav-${s}`}
                  >
                    {t.nav[s]}
                  </button>
                ))}
              </nav>
              <a
                href={bookLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-gold px-6 py-3.5 text-base font-medium text-white"
                data-testid="mobile-book-button"
              >
                {t.nav.book}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
